import { useMemo } from 'react'
import { UseFormGetFieldState } from 'react-hook-form'
import { ActionDetail, FormatedPatientFormData, PatientIdentityStatusEnum } from '../_props'
import { usePatientFormContext } from '../../context/PatientFormContext'
import { getIdentityStatusSlug } from '../PatientIdentity/_utils/_utils'
import { RegradeIdentityMethod, RegradeIdentityMethodEnum } from './_props'
import useAutomaticIdentityValidation from '../../../../hooks/useAutomaticIdentityValidation'

const usePatientUtils = () => {
    const checkUpdatedFields = async (
        initialValues: any,
        submittedValues: any,
        getFieldState: UseFormGetFieldState<FormatedPatientFormData>,
    ) => {
        const updatedFields: ActionDetail[] = []
        const watchedFields = [
            { control: 'address.street', label: 'Numéro et voie' },
            { control: 'address.postalCode', label: 'Code postal' },
            { control: 'address.city', label: 'Ville' },
            { control: 'birthCity', label: 'Code lieu de naissance (Code INSEE)' },
            { control: 'birthDate', label: 'Date de naissance' },
            { control: 'birthName', label: 'Nom de naissance' },
            { control: 'contactEmail', label: 'Email' },
            { control: 'contactPhone', label: 'Numéro de téléphone portable' },
            { control: 'countryBirth', label: 'Pays de naissance' },
            { control: 'firstFirstName', label: '1er prénom de naissance' },
            { control: 'firstName', label: 'Prénom utilisé' },
            { control: 'firstNameList', label: 'Prénom(s) de naissance' },
            { control: 'hasCss', label: 'CSS' },
            { control: 'lastName', label: 'Nom utilisé' },
            { control: 'hasLtDiseases', label: 'ALD' },
            { control: 'patientIsSameAsInsured', label: 'Le patient est-il l’assuré ?' },
            { control: 'insuredSsn', label: 'Numéro de sécurité sociale de l’assuré' },
            { control: 'patientInsuredUserLabel', label: "Nom et prénom de l'assuré(e)" },
            { control: 'socialSecurityNumber', label: 'Numéro de sécurité sociale' },
            {
                control: 'attendingPhysician',
                valuePath: 'attendingPhysician.physician.fullName',
                label: 'Médecin traitant du patient',
            },
            { control: 'room', label: 'Numéro de chambre' },
            { control: 'sex', label: 'Sexe' },
            { control: 'identityProof', label: "Justificatif d'identité" },
            { control: 'insiIdentityAttributes', label: "Nature de l'identité" },
        ] as const

        watchedFields.forEach((field) => {
            if (getFieldState(field.control).isDirty) {
                const valuePath = 'valuePath' in field ? field.valuePath : field.control
                updatedFields.push({
                    fieldName: field.label,
                    oldValue: valuePath.split('.').reduce((acc, key) => acc?.[key], initialValues),
                    newValue: valuePath.split('.').reduce((acc, key) => acc?.[key], submittedValues),
                })
            }
        })

        return updatedFields
    }

    return {
        checkUpdatedFields,
    }
}

const identitySlugs = (...x: PatientIdentityStatusEnum[]) => x

export const useRegradeIdentity = () => {
    const { state } = usePatientFormContext()
    const { identityStatus } = state.ins
    const { isAutomaticIdentityValidationDisabled } = useAutomaticIdentityValidation()

    const identityStatusSlug = useMemo(
        () => getIdentityStatusSlug(identityStatus?.code ?? undefined),
        [identityStatus?.code],
    )

    const regradeIdentity = (
        method: RegradeIdentityMethodEnum,
        identityProofCode?: number,
        slugToSet?: PatientIdentityStatusEnum | PatientIdentityStatusEnum[],
    ) => {
        const isTrustedIdentityProof = [2, 3, 4, 5].includes(identityProofCode ?? 1)

        const methods: Record<RegradeIdentityMethodEnum, RegradeIdentityMethod> = {
            downgrade: () => {
                if (identityStatusSlug === 'QUALIFIED') {
                    return isTrustedIdentityProof ? 'VALIDATED' : 'PROVISIONAL'
                }
                if (identityStatusSlug === 'RECOVERED') {
                    return 'PROVISIONAL'
                }
                if (identityStatusSlug === 'VALIDATED') {
                    return 'PROVISIONAL'
                }
                return 'PROVISIONAL'
            },
            reprocess: () => {
                if (identityStatusSlug) {
                    if (isTrustedIdentityProof) {
                        if (isAutomaticIdentityValidationDisabled) {
                            if (identityStatusSlug === 'QUALIFIED') {
                                return 'RECOVERED'
                            }
                            if (identityStatusSlug === 'VALIDATED') {
                                return 'PROVISIONAL'
                            }
                        } else {
                            return identitySlugs('QUALIFIED', 'RECOVERED').includes(identityStatusSlug)
                                ? 'QUALIFIED'
                                : 'VALIDATED'
                        }
                    }
                    return identitySlugs('QUALIFIED', 'RECOVERED').includes(identityStatusSlug)
                        ? 'RECOVERED'
                        : 'PROVISIONAL'
                }
                return 'PROVISIONAL'
            },
            upgrade: () => {
                if (identityStatusSlug === 'VALIDATED') {
                    return 'QUALIFIED'
                }
                if (identityStatusSlug === 'PROVISIONAL') {
                    return 'VALIDATED'
                }
                if (identityStatusSlug === 'RECOVERED') {
                    return 'QUALIFIED'
                }
                if (identityStatusSlug === 'QUALIFIED') {
                    return 'QUALIFIED'
                }
                return 'PROVISIONAL'
            },
            set: (slugs) => {
                if (!slugs) return 'PROVISIONAL'

                if (Array.isArray(slugs)) {
                    if (isTrustedIdentityProof) {
                        return slugs[0]
                    }

                    return slugs[1] ? slugs[1] : slugs[0]
                }

                return slugs
            },
        }

        return methods[method](slugToSet)
    }

    return {
        regradeIdentity,
    }
}

export const usePatientIdentity = () => {
    const { state } = usePatientFormContext()
    const currentIdentityStatus = state.ins.identityStatus
    const currentIdentityStatusSlug = useMemo(
        () => getIdentityStatusSlug(currentIdentityStatus?.code ?? undefined),
        [currentIdentityStatus?.code],
    )

    return {
        currentIdentityStatus,
        currentIdentityStatusSlug,
    }
}

export default usePatientUtils
